// Cropping of note images (Edit → image menu → Crop). The image file is never changed: the crop is
// stored on the image as data-crop="top right bottom left" (percent of the original image cut off
// on each side) and the image is shown through a frame that clips it, like Word's picture crop.
// Images without data-crop are shown exactly as before.

export interface ImageCrop {
  /** Fractions (0..1) of the original image cut off on each side. */
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** At least this fraction of the image stays visible in each direction. */
export const MIN_VISIBLE_FRACTION = 0.05;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));
const round4 = (value: number): number => Math.round(value * 10000) / 10000;

/** A valid crop from an attribute value (object of fractions, or "t r b l" in percent), or null when
 * nothing is cropped. Out-of-range values are clamped so the visible part never vanishes. */
export function normalizeImageCrop(value: unknown): ImageCrop | null {
  let parts: number[] | null = null;
  if (typeof value === 'string') {
    const numbers = value.trim().split(/[\s,]+/).map(Number);
    if (numbers.length === 4 && numbers.every(Number.isFinite)) parts = numbers.map((n) => n / 100);
  } else if (value && typeof value === 'object') {
    const v = value as Partial<ImageCrop>;
    const numbers = [v.top, v.right, v.bottom, v.left].map(Number);
    if (numbers.every(Number.isFinite)) parts = numbers;
  }
  if (!parts) return null;

  const max = 1 - MIN_VISIBLE_FRACTION;
  let [top, right, bottom, left] = parts.map((n) => clamp(n, 0, max));
  if (left + right > max) {
    const scale = max / (left + right);
    left *= scale;
    right *= scale;
  }
  if (top + bottom > max) {
    const scale = max / (top + bottom);
    top *= scale;
    bottom *= scale;
  }
  const crop = { top: round4(top), right: round4(right), bottom: round4(bottom), left: round4(left) };
  return isImageCropActive(crop) ? crop : null;
}

export function isImageCropActive(crop: ImageCrop | null | undefined): crop is ImageCrop {
  return !!crop && (crop.top > 0 || crop.right > 0 || crop.bottom > 0 || crop.left > 0);
}

/** The data-crop attribute value ("t r b l" in percent), or null when nothing is cropped. */
export function serializeImageCrop(value: unknown): string | null {
  const crop = normalizeImageCrop(value);
  if (!crop) return null;
  const pct = (n: number) => String(Math.round(n * 10000) / 100);
  return [crop.top, crop.right, crop.bottom, crop.left].map(pct).join(' ');
}

export interface ImageCropLayout {
  /** Visible fraction of the original width / height. */
  visibleWidth: number;
  visibleHeight: number;
  /** Width / height of the visible part, in original pixels. */
  aspectRatio: number;
  /** Natural width of the visible part (px) — the frame's width when the image has no set width. */
  naturalVisibleWidth: number;
  /** Image size and offset inside the frame, in percent of the frame. */
  imgWidthPct: number;
  imgHeightPct: number;
  imgLeftPct: number;
  imgTopPct: number;
}

/** How to place the whole image inside a frame showing only the cropped part; null when the image
 * isn't cropped or its natural size is unknown (it is then shown uncropped). */
export function imageCropLayout(cropValue: unknown, naturalWidth: unknown, naturalHeight: unknown): ImageCropLayout | null {
  const crop = normalizeImageCrop(cropValue);
  const nw = Number(naturalWidth);
  const nh = Number(naturalHeight);
  if (!crop || !(nw > 0) || !(nh > 0)) return null;
  const visibleWidth = 1 - crop.left - crop.right;
  const visibleHeight = 1 - crop.top - crop.bottom;
  return {
    visibleWidth,
    visibleHeight,
    aspectRatio: (nw * visibleWidth) / (nh * visibleHeight),
    naturalVisibleWidth: Math.round(nw * visibleWidth),
    imgWidthPct: 100 / visibleWidth,
    imgHeightPct: 100 / visibleHeight,
    imgLeftPct: (-100 * crop.left) / visibleWidth,
    imgTopPct: (-100 * crop.top) / visibleHeight,
  };
}

const num = (n: number): string => String(Math.round(n * 1000) / 1000);

/** Inline style of the clipping frame. `width`/`height` are CSS lengths of the visible part (height
 * only when the aspect ratio isn't kept); `border` an optional CSS border. */
export function imageCropFrameStyle(layout: ImageCropLayout, width: string | null, height: string | null, border: string | null): string {
  const parts = [
    'display: inline-block',
    'position: relative',
    'overflow: hidden',
    'max-width: 100%',
    'line-height: 0',
    'vertical-align: top',
    'box-sizing: content-box',
    `width: ${width || `${layout.naturalVisibleWidth}px`}`,
  ];
  if (height) parts.push(`height: ${height}`);
  else parts.push(`aspect-ratio: ${num(layout.aspectRatio)}`);
  if (border) parts.push(`border: ${border}`);
  return parts.join('; ');
}

/** Inline style of the image inside the frame. */
export function imageCropImgStyle(layout: ImageCropLayout): string {
  return [
    'position: absolute',
    'display: block',
    'max-width: none',
    'max-height: none',
    'margin: 0',
    'border: 0',
    `width: ${num(layout.imgWidthPct)}%`,
    `height: ${num(layout.imgHeightPct)}%`,
    `left: ${num(layout.imgLeftPct)}%`,
    `top: ${num(layout.imgTopPct)}%`,
  ].join('; ');
}
